import React, { useState, useEffect, useContext } from 'react';
import ReactGA from "react-ga4";
import * as cheerio from 'cheerio';
import VerticalResultTable from './VerticalResultTable';
import HorizontalResultTable from './HorizontalResultTable';
import { AppContext } from '../App.js';

function encodeParam(paramList){
  let ss = [];
  for(let [key, value] of paramList){
    ss.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
  }
  return ss.join('&');
}

/*
function toKatakana(str){
  return str.replace(/[\u3041-\u3096]/g, (c) => String.fromCharCode(c.charCodeAt()+0x60));
}
*/

function normalizeHinshi(h){
  let conditions = h === "形容動詞" ? String.raw`名詞-普通名詞-形状詞可能%" OR 品詞 LIKE "形状詞` : h;
  return `( 品詞 LIKE "${conditions}%" )`;
}
function getQueryEqValue(k, v){
  let ret = '( ';
  for(let value of v) {
    ret += `${k}="${value}" OR `
  }
  ret = ret.slice(0, -3) + ")";
  return ret;
}
function createKeyParam(param, hinshi){
  let eq = Object.entries(param.eq).map(([key, value]) => getQueryEqValue(key, value));
  let like = Object.entries(param.like).map(([key, value]) => `${key} LIKE "${value}%"`);
  return `(${ eq.join(' AND ') } AND ${ like.join(' AND ') } AND ${normalizeHinshi(hinshi)})` 
}

export default function CorpusTable({ word, title }) {
  const [result, setResult] = useState(null); // result of API
  const [error, setError] = useState(null);
  const [vertical, setVertical, csrf] = useContext(AppContext);

  function handleResult(json){ // 結果の受け取り
    // console.log(json);
    let res = [];
    for(let row of json.result.rows) {
      res.push({
        before: row.lcontext ? row.lcontext.map(e => e.orth_token).join('') : "",
        word: row.keyword ? row.keyword.map(e => e.orth_token).join('') : "",
        after: row.rcontext ? row.rcontext.map(e => e.orth_token).join('') : "",
        cform: row.cform ? row.cform.replace(/-一般/,"") : "",
        title: row.title ? row.title : "",
        subtitle: row.subtitle ? row.subtitle : "",
        link: (row.external_links && row.external_links.length) ? row.external_links[0].url : ""
      })
    }
    setResult(res);
  }  

  // This procedure is called if word or title are changed
  useEffect(() => {
    const ERROR_MESSAGE_NOT_LOGIN =
      `中納言にログインしていません。<a target="_blank" href=${process.env.REACT_APP_AUTH_ENDPOINT}>こちら</a>から中納言にログインしてください。`;
    const ERROR_MESSAGE_FORBIDDEN = //
      `CHJ 中納言を利用することができません。中納言にログインしているか、<a target="_blank" href=${process.env.REACT_APP_AUTH_ENDPOINT}>こちら</a>から確認してください。`;
    const ERROR_MESSAGE_NO_AUTHORITY = 'ログインしているユーザアカウントでは CHJ 中納言を利用することができません。';
    const ERROR_MESSAGE_SERVER_ERROR = '中納言サーバエラー';

    setResult(null);
    setError(null);

    // Google Analytics 4
    if (ReactGA.isInitialized) {
      ReactGA.event('search')
    }

    /**
     * エラー処理
     * @param {string} message
     */
    function handleError(message) {
      // useEffect の外に出すと、eslint(react-hooks/exhaustive-deps) が出る。
      // （csrf.current の値変更があるため）
      setError({ message: message });
      // エラーの場合はセッション切れの可能性があるので、CSRF トークンを削除する。
      // （次回検索時には CSRF トークン取得を行う）
      csrf.current = {};
    }

    /**
     * 検索リクエスト（メソッド POST）を実行する
     * @param {XMLHttpRequest} xhr
     */
    function postSearch(xhr) {
      xhr.open("POST", process.env.REACT_APP_API_ENDPOINT, true);
      xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded; charset=UTF-8");
      if (csrf.current.key) {
        xhr.setRequestHeader(csrf.current.key, csrf.current.value);
      }
      xhr.withCredentials = true;

      xhr.onreadystatechange = function () { // Call a function when the state changes.
        if (this.readyState === XMLHttpRequest.DONE) {
          if (this.status === 200) {
            // Request finished. Do processing here.
            try {
              handleResult(JSON.parse(this.responseText));
            } catch {
              if (this.responseURL?.includes('/auth/login')) {
                handleError(ERROR_MESSAGE_NOT_LOGIN);
              } else if (this.responseURL?.endsWith('/authorizationFailed')) {
                handleError(ERROR_MESSAGE_NO_AUTHORITY);
              } else {
                handleError(`${ERROR_MESSAGE_SERVER_ERROR} (${this.status})`);
              }
            }
          } else if (this.status === 403) {
            handleError(ERROR_MESSAGE_FORBIDDEN);
          } else {
            handleError(`${ERROR_MESSAGE_SERVER_ERROR} (${this.status})`);
          }
        }
      }

      let coreExpr = title === "今昔物語集" ? ' AND core="true"' : "" ;
      xhr.send(encodeParam([
        ["condDescription", "検索条件式で検索"],
        ["retrievalMethod", "短単位検索"],
        ["queryExpr", `キー: ${createKeyParam(word.search, word.hinshi)} IN 作品名="${title}"${coreExpr}`]
      ]));
    }

    // Use legacy XHR due to Canellation
    const xhrGet = new XMLHttpRequest();  // GET で使う XHR
    const xhr = new XMLHttpRequest();  // POST で使う XHR

    if (Object.keys(csrf.current).length === 0) {
      // CSRF トークンが無い場合は GET で取得する。
      xhrGet.open('GET', process.env.REACT_APP_API_ENDPOINT, true);
      xhrGet.withCredentials = true;
      xhrGet.onreadystatechange = function () { // Call a function when the state changes.
        if (this.readyState === XMLHttpRequest.DONE) {
          if (this.responseURL?.includes('/auth/login')) {
            handleError(ERROR_MESSAGE_NOT_LOGIN);
            return;  // リクエストは完了しているので、キャンセル処理は不要。
          } else if (this.responseURL?.endsWith('/authorizationFailed')) {
            handleError(ERROR_MESSAGE_NO_AUTHORITY);
            return;  // リクエストは完了しているので、キャンセル処理は不要。
          } else if (this.status === 403) {
            handleError(ERROR_MESSAGE_FORBIDDEN);
            return;  // リクエストは完了しているので、キャンセル処理は不要。
          } else if (this.status !== 200) {
            handleError(`${ERROR_MESSAGE_SERVER_ERROR} (${this.status})`);
            return;  // リクエストは完了しているので、キャンセル処理は不要。
          }
          const $ = cheerio.load(this.responseText);
          const csrfHeader = $('meta[name="_csrf_header"]').attr('content') ?? '';
          const csrfToken = $('meta[name="_csrf"]').attr('content') ?? '';
          // csrf の値を変えることで useEffect() が再実行される。
          if (csrfHeader) {
            csrf.current = { key: csrfHeader, value: csrfToken };
          } else {
            // _csrf_header が無い場合
            // 何もしないと、CSRF トークンを使わずに POST する。
            // CHJ 中納言の検索で CSRF が無効の場合は、検索可否は他の条件（認証など）次第。
            // CHJ 中納言の検索で CSRF が有効の場合は、(POST で) 検索が失敗する。
            // ひとまず何もしないことにする。
            // （CHJ 中納言の検索で CSRF が有効になった場合は、ここでエラーにすれば、失敗するとわかっている POST を実行しないで済む）
            //
            // 追記：CSRF が無効の場合、GET のレスポンスに CSRF トークンが無い場合がありうるので、ここでエラーにしてはいけない。
            // （ CHJ 中納言からログアウト後、検索を実行 → 中納言にログイン → 検索を実行した時には、
            //    GET に対して以前の POST の結果の JSON を返す。この場合は GET のレスポンスに CSRF トークンが含まれない）
          }
          postSearch(xhr);
        }
      }
      xhrGet.send();
    } else {
      // CSRF トークンが取得済の場合は POST する。
      postSearch(xhr);
    }

    return () => {
      // request is cancelled if word or title are changed
      console.log("cancelled");
      xhrGet.abort();
      xhr.abort();
    };
  }, [word, title, csrf]);

  if (result === null && error === null) { // Loading
    return <div className="d-flex justify-content-center">
      <div className="spinner-border text-light m-5" style={{width: "5rem", height: "5rem"}} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  } else if (error !== null) { // Error あり
    return <div className="panel">
      <span dangerouslySetInnerHTML={{ __html: error.message }} />
    </div>;
  } else { // Show results
    let header = <button className="btn btn-info" onClick={() => setVertical(!vertical)}>{vertical ? "横書き" : "縦書き"}に切り替える</button>;
    return <>
      { (vertical) ? 
          <VerticalResultTable result={result} header={header} /> 
        : <HorizontalResultTable result={result} header={header} /> }
    </>;
  }
}
